import { Component, OnInit, OnDestroy, inject, DestroyRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormGroup,
  FormControl,
  Validators,
} from '@angular/forms';
import { CardModule } from 'primeng/card';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/textarea';
import { Select } from 'primeng/select';
import { ButtonModule } from 'primeng/button';
import { DatePicker } from 'primeng/datepicker';
import { TooltipModule } from 'primeng/tooltip';
import { Toast } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  Subject,
  takeUntil,
} from 'rxjs';
import { FilingFormStateService } from '../../../services/mbrs/filing-form-state.service';
import { FilingModeService } from '../../../services/mbrs/filing-mode.service';
import { FieldWarningService } from '../../../services/mbrs/field-warning.service';
import { WarningResult, WarningRule } from '../../../models/validation-warning.model';
import { EnvironmentService } from '../../../services/common/environment.service';
import { SubmitForValidationComponent } from "../../common/submit-for-validation/submit-for-validation.component";
import { RichTextFieldComponent } from '../../common/rich-text-field/rich-text-field.component';
import { WarningMessageComponent, WarningMessage } from "../../common/warning-message/warning-message.component";
import { FinancialPeriodBandComponent } from "../../common/financial-period-band/financial-period-band.component";
import { handleDateInputChange, getFieldMaxLength } from '../../../utils/helpers';
import {
  applyInputTransform as applyInputTransformToField,
  applyInputTransformsToForm,
  onPasteInputTransform as pasteInputTransformToField,
  type InputTransform,
} from '../../../utils/input-transform.util';
import { FORM_AUTOSAVE_DEBOUNCE_MS } from '../../../utils/debounce-times.util';
import { buildFormEditPipeline } from '../../../utils/form-edit-pipeline.util';
import { KeyFilterModule } from 'primeng/keyfilter';
import {
  identificationTypeFieldFor,
  isNumericOnlyIdentificationField,
} from '../../../utils/identification-field.util';
import { evaluateConditional } from '../../../services/mbrs/conditional/conditional-field.evaluator';
import { SHEET_KEYS } from '../../../models/sheet-keys';
import {
  TOAST_DURATION_DEFAULT_MS,
  TOAST_DURATION_LONG_MS,
} from '../../../utils/toast-durations.util';
import { ConditionalRule } from '../../../services/mbrs/conditional/conditional-field.model';
import { finalizeSheetInit } from '../../../utils/sheet-init-finalize.util';
import { PlaceholderPipe, FixedPlaceholderPipe } from '../../../utils/pipes/placeholder.pipe';
import { SelectClearIconBehaviorDirective } from '../../../directives/select-clear-icon-behavior.directive';
import { RequiredMarkerComponent } from '../../common/required-marker/required-marker.component';
import { isControlMandatory } from '../../../utils/required-field.util';

import { SheetLoaderComponent } from '../../common/sheet-loader/sheet-loader.component';
import { FieldLinkTargetDirective } from '../../../directives/field-link-target.directive';
import { computedControlPredicate, revealValidationErrors } from '../../../utils/validation-reveal.util';
import { adoptIntoAutoSaveSheet } from '../../../utils/adopt-remote-sheet-state.util';
import {
  LoadTrace,
  NO_OP_LOAD_TRACE,
  SheetLoadTraceService,
} from '../../../services/mbrs/sheet-load-trace.service';
import { SHEET_LOAD_STEPS } from '../../../utils/sheet-load-trace.util';
import {
  classifySheetInitError,
  fetchJsonAsset,
  fetchSheetSchema,
} from '../../../utils/schema-asset.util';
interface FormSchema {
  role: string;
  definition: string;
  sheetId: string;
  label: string;
  controls: FormControlSchema[];
}

interface FormControlSchema {
  qname: string;
  label: string;
  type: string;
  ui: {
    control: string;
    placeholder?: string;
    enumSource?: string;
    readonly?: boolean;
    defaultValue?: string;
    rows?: number;
    auditorTable?: boolean;
    auditorRow?: string;
    auditorColumns?: string[];
    inputFilter?: 'numeric' | 'alpha' | 'alphanumeric' | 'alphanumeric-space' | 'alphanumeric-dash';
    inputTransform?: InputTransform[];
    maxLength?: number;
  };
  required?: boolean;
  conditional?: ConditionalRule;
  validators?: Array<{ name: string; pattern?: string; patternSource?: string }>;
  error?: { [key: string]: string };
  warnings?: WarningRule[];
}

@Component({
  selector: 'app-role7-130000-auditors-report',
  standalone: true,
  imports: [
    SheetLoaderComponent,
    RequiredMarkerComponent,
    CommonModule,
    ReactiveFormsModule,
    CardModule,
    InputTextModule,
    Textarea,
    Select,
    SelectClearIconBehaviorDirective,
    ButtonModule,
    DatePicker,
    TooltipModule,
    Toast,
    SubmitForValidationComponent,
    RichTextFieldComponent,
    WarningMessageComponent,
    FinancialPeriodBandComponent,
    KeyFilterModule,
    PlaceholderPipe,
    FixedPlaceholderPipe,
    FieldLinkTargetDirective
  ],
  providers: [MessageService],
  templateUrl: './role7-130000-auditors-report.component.html',
  styleUrls: ['./role7-130000-auditors-report.component.css'],
})
export class Role7130000AuditorsReportComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private destroyRef = inject(DestroyRef);
  private stateService = inject(FilingFormStateService);
  private messageService = inject(MessageService);
  private loadTrace = inject(SheetLoadTraceService);
  /**
   * This mount's load trace. Opened as the first act of `ngOnInit` and closed
   * either by the terminal step mark or by the init catch, so a failure reports
   * which of the seven load steps this sheet actually reached.
   *
   * @see src/app/utils/sheet-load-trace.util.ts
   */
  private loadTraceHandle: LoadTrace = NO_OP_LOAD_TRACE;
  private filingModeService = inject(FilingModeService);
  private warningService = inject(FieldWarningService);
  private envService = inject(EnvironmentService);

  /** Check if the current environment is development (for showing debug info like role IDs) */
  get isDev(): boolean {
    return this.envService.isDevelopment();
  }

  // Expose isEditedMode signal to template for conditional asterisk display
  isEditedMode = this.filingModeService.isEditedMode;

  // Store validation warnings per field (qname -> warnings array)
  fieldWarnings: { [qname: string]: WarningResult[] } = {};

  auditorsReportForm!: FormGroup;
  schema!: FormSchema;
  typeDict: any = {};
  isLoading = true;

  // Dropdown options
  dropdownOptions: { [key: string]: any[] } = {};

  // Flag to track if we're loading saved state
  private isLoadingState = false;

  /**
   * Set once this sheet's initial load has settled. Past that point
   * `setupStateListener` stops re-patching the form — see its docblock for why
   * a post-init repaint blanks a date the user is half-way through typing.
   * Mirrors role23/role24, which carry the identical listener and guard.
   */
  private isInitialLoadComplete = false;

  // Dynamic form title with Type of Submission
  /**
   * Card header carrying the filing's submission type, e.g.
   * "Auditors Report (FS-MPERS)".
   *
   * Two rules, both of which this sheet used to break:
   *  - the name comes from the canonical `SHEET_DISPLAY_NAMES` map (via
   *    `getSheetTitleWithSubmissionType`), never a literal repeated here;
   *  - it is a LIVE getter, never an `ngOnInit` snapshot — see the cold-mount
   *    rule on `FilingFormStateService.getFormTitleWithSubmissionType()`.
   */
  get formTitle(): string {
    return this.stateService.getSheetTitleWithSubmissionType(
      SHEET_KEYS.auditorsReport
    );
  }

  async ngOnInit(): Promise<void> {
    // Open this mount's load trace before anything else, so a failure
    // anywhere below reports which load steps were reached.
    this.loadTraceHandle = this.loadTrace.beginSheetLoad(
      '7',
      this.stateService.getConversionId(),
      this.destroyRef,
    );
    try {
      await this.loadSchema();
      await this.loadTypeDict();
      this.buildForm();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.formBuilt);
      this.applyConditionalRequired();
      this.auditorsReportForm.valueChanges
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.applyConditionalRequired());
      this.loadSavedState();
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
      this.setupStatePersistence();
      this.setupStateListener();
      this.setupWarningEvaluation(); // Setup warning evaluation on form changes
      this.setupCrossSheetWarningListener(); // Listen for cross-sheet changes
      this.evaluateFieldWarnings(); // Evaluate warnings on initial load

      // Deterministic replacement for the legacy in-loadSavedState
      // setTimeout(100 → 600) finalization. Extracted to finalizeInit() so the
      // non-init caller (onCancelConfirmed) also re-finalizes after its own
      // loadSavedState() call — see finalizeInit's doc comment.
      await this.finalizeInit();

      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    } catch (error) {
      // Report through the trace instead of a fixed sentence. The toast now
      // names this sheet, the steps it reached, and whether the schema was
      // missing, unreadable, or fine but the form could not be built —
      // `classifySheetInitError` infers the last of those by elimination.
      // Must run BEFORE the loading flag is cleared below: `fail()` closes the
      // trace, which is what stops the finalize mark on the next line from
      // recording this failed init as a success.
      this.loadTraceHandle.fail(classifySheetInitError(error), error);
      this.isLoading = false;
      this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.finalized);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Setup listener for external state changes (e.g., data loaded from backend)
   * This allows the form to react when the container populates data via the state service
   */
  private setupStateListener(): void {
    this.stateService.state$
      .pipe(
        map((state) => state.auditorsReport),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((auditorsReportData) => {
        if (auditorsReportData && Object.keys(auditorsReportData).length > 0) {
          // After initial load completes, skip re-patching. `state$` is the
          // MODIFIED stream, so this sheet's own debounced persist comes
          // straight back here — and `patchValue` reaches PrimeNG's
          // `writeValue`, which re-renders the input from the model even under
          // `emitEvent: false`. While a date is half-typed that model is null
          // (`DatePicker.onUserInput` calls `updateModel(null)` on text its
          // `parseValueFromString` rejects), so the echo blanks the digits the
          // user just entered. Adopting a SIBLING TAB's values is
          // `adoptRemoteState()`'s job, on `remoteUpdateApplied$`.
          // Mirrors role12/role23/role24. See isInitialLoadComplete.
          if (this.isInitialLoadComplete) {
            return;
          }

          // Temporarily set isLoadingState to prevent auto-save during external data load
          this.isLoadingState = true;

          // Convert date string to Date object for PrimeNG DatePicker
          const processedData = { ...auditorsReportData };
          if (processedData['ssmt:DateOfSigningAuditorsReport']) {
            processedData['ssmt:DateOfSigningAuditorsReport'] = new Date(
              processedData['ssmt:DateOfSigningAuditorsReport']
            );
          }

          // Patch the form with the new data
          this.auditorsReportForm.patchValue(processedData, {
            emitEvent: false,
          });

          // IMPORTANT: Apply conditional required-state so hidden fields are not validated
          this.applyConditionalRequired();

          // Re-enable auto-save after a short delay
          setTimeout(() => {
            this.isLoadingState = false;
          }, 100);
        }
      });
  }

  /**
   * Load the form schema from JSON
   */
  private async loadSchema(): Promise<void> {
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaSelected);
    this.schema = await fetchSheetSchema<FormSchema>(
      this.loadTraceHandle,
      '/assets/forms/role-130000.form.json',
    );
    this.loadTraceHandle.mark(SHEET_LOAD_STEPS.schemaParsed);
  }

  /**
   * Load type dictionary for dropdown options
   */
  private async loadTypeDict(): Promise<void> {
    // FATAL by design: without the dictionary the dropdowns have no options and
    // the conditional mappings cannot run, so the sheet must not open as if it had
    // loaded. The typed error propagates to the sheet's single catch, which
    // reports ASSET / PARSE / NET through the load trace.
    this.typeDict = await fetchJsonAsset<any>('/assets/types.dict.json');
    this.buildDropdownOptions();
  }

  /**
   * Build dropdown options from type dictionary
   */
  private buildDropdownOptions(): void {
    if (!this.typeDict.enums) return;

    Object.keys(this.typeDict.enums).forEach((key) => {
      this.dropdownOptions[key] = this.typeDict.enums[key].map(
        (value: string) => ({
          label: value,
          value: value,
        })
      );
    });
  }

  /**
   * Build the reactive form from schema
   */
  private buildForm(): void {
    if (!this.schema || !this.schema.controls) {
      console.error('Schema not loaded - cannot build form');
      throw new Error('Schema must be loaded before building form');
    }
    const formControls: { [key: string]: FormControl<any> } = {};

    this.schema.controls.forEach((control: FormControlSchema) => {
      const validators = [];

      // Add required validator
      if (control.required) {
        validators.push(Validators.required);
      }

      // Add pattern validator if specified (using patternSource like Role-2)
      const patternValidator = control.validators?.find(
        (validator) => validator.name === 'pattern'
      );
      if (patternValidator && patternValidator.patternSource) {
        const patternKey = patternValidator.patternSource.replace('type:', '');
        const pattern = this.typeDict.patterns?.[patternKey];
        if (pattern) {
          validators.push(Validators.pattern(pattern));
        }
      } else if (patternValidator && patternValidator.pattern) {
        // Fallback: If pattern is directly provided instead of patternSource
        validators.push(Validators.pattern(patternValidator.pattern));
      }

      // Get default value
      let defaultValue = '';
      if (control.ui.defaultValue) {
        defaultValue = control.ui.defaultValue;
      }

      // Create form control
      const formControl = new FormControl(
        { value: defaultValue, disabled: control.ui.readonly || false },
        validators
      );

      formControls[control.qname] = formControl;
    });

    this.auditorsReportForm = new FormGroup(formControls);
  }

  /**
   * Single source for conditional required-state: for every control with a
   * `validation`/`both` conditional, arm `Validators.required` when the rule is
   * active, else clear. Reads the same evaluator as shouldShowField/the projector.
   */
  private applyConditionalRequired(): void {
    if (!this.schema?.controls) return;
    const data = this.auditorsReportForm.getRawValue();
    const context = { filingInfo: this.stateService.getActiveFilingInfoData() };
    for (const control of this.schema.controls) {
      const rule = control.conditional;
      if (!rule || rule.appliesTo === 'visibility') continue;
      const formControl = this.auditorsReportForm.get(control.qname);
      if (!formControl) continue;
      const { validationActive } = evaluateConditional(rule, data, context);
      if (validationActive) {
        formControl.setValidators([Validators.required]);
      } else {
        formControl.clearValidators();
      }
      formControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  /**
   * Load saved state from state service
   */
  private loadSavedState(): void {
    this.isLoadingState = true;
    this.stateService.markSheetInitializing(SHEET_KEYS.auditorsReport);
    const savedData = this.stateService.getSheetData(SHEET_KEYS.auditorsReport);

    if (savedData && Object.keys(savedData).length > 0) {
      // Convert date strings back to Date objects for PrimeNG DatePicker
      const processedData = { ...savedData };
      if (processedData['ssmt:DateOfSigningAuditorsReport']) {
        processedData['ssmt:DateOfSigningAuditorsReport'] = new Date(
          processedData['ssmt:DateOfSigningAuditorsReport']
        );
      }

      this.auditorsReportForm.patchValue(processedData, { emitEvent: false });
    }

    // NOTE: the isLoadingState flip + baseline snapshot
    // (markSheetInitializedWithStatus) that used to live here in a
    // setTimeout(100 → 600) chain are now owned by finalizeSheetInit(),
    // invoked via finalizeInit() after waitForMbrsHydration(). We keep ONLY the
    // synchronous prefix above (markSheetInitializing + emitEvent:false patch).
  }

  /**
   * Deterministic sheet-init finalization. Must run after EVERY loadSavedState()
   * (init AND cancel/revert) — the legacy setTimeout lived inside loadSavedState
   * and re-finalized on each call. Safe: all init-time patches use emitEvent:false.
   *
   * NB: persistInitial below re-baselines via updateSheetData(...,true) — the
   * legacy timer block did NOT do this, so this replays behaviour the timer
   * never had, it does not omit anything the timer used to do.
   *
   * Invariant: persistInitial MUST use the same accessor as this sheet's own
   * auto-save persist. This sheet (and role4/role6) currently pair `.value` in
   * both places — internally consistent and inert only because their schemas
   * (role-120000/120200/130000.form.json) declare no readonly/disabledWhen
   * controls today. The repo-wide default is `getRawValue()` (see
   * docs/troubleshooting/troubleshooting.md § "Form data" → "Form values
   * silently drop disabled controls"; Common Pitfall #2 in root CLAUDE.md) —
   * role0/role2 pair `getRawValue()` in both because their schemas DO have
   * readonly controls. If this sheet ever gains a readonly/disabledWhen
   * control, move BOTH this `persistInitial` AND its auto-save persist to
   * `getRawValue()` together — never just one, or `.value` silently stops
   * refreshing the disabled control's key and reintroduces the permanent
   * modified-vs-original divergence the pencil-reconcile hotfix removed.
   */
  private async finalizeInit(): Promise<void> {
    await finalizeSheetInit({
      trace: this.loadTraceHandle,
      stateService: this.stateService,
      sheetKey: SHEET_KEYS.auditorsReport,
      form: this.auditorsReportForm,
      setLoadingState: (isLoading) => { this.isLoadingState = isLoading; },
      isComputed: computedControlPredicate(this.schema?.controls),
      // Re-baseline the transformed form value (esp. the rich-text field's
      // on-load fragment normalization) so the snapshot equals what the auto-save
      // persist writes; without it a full revert never cleared the pencil.
      persistInitial: () => {
        this.stateService.updateSheetData(
          SHEET_KEYS.auditorsReport,
          this.auditorsReportForm.value,
          this.auditorsReportForm.valid,
          true, // skipEditedMark — re-baseline, not a user edit
        );
      },
    });

    // Initial load has settled — from here the state listener stops re-patching.
    this.isInitialLoadComplete = true;
  }

  /**
   * Setup auto-save on form changes
   */
  private setupStatePersistence(): void {
    buildFormEditPipeline({
      source$: this.auditorsReportForm.valueChanges,
      debounceMs: FORM_AUTOSAVE_DEBOUNCE_MS,
      destroyRef: this.destroyRef,
      shouldSkip: () => this.isLoadingState,
      persist: () => {
        // Seam 3 of `ui.inputTransform`: put the form on the same canonical value the
        // state will hold BEFORE reading `.value` / `.valid`, so validation and the
        // submitted value cannot disagree. See applyInputTransformsToForm.
        applyInputTransformsToForm(this.auditorsReportForm);
        this.stateService.updateSheetData(
          SHEET_KEYS.auditorsReport,
          this.auditorsReportForm.value,
          this.auditorsReportForm.valid
        );
      },
      evaluateStatus: () => {
        this.stateService.updateSheetLevelStatus(SHEET_KEYS.auditorsReport, { hasErrors: !this.auditorsReportForm.valid });
      },
    });
  }

  /**
   * Submit form
   */
  submitForm(): void {
    if (this.auditorsReportForm.invalid) {
      this.auditorsReportForm.markAllAsTouched();
      this.messageService.add({
        severity: 'error',
        summary: 'Validation Error',
        detail: 'Please fill in all required fields',
        life: TOAST_DURATION_LONG_MS,
      });
      return;
    }

    const formData = this.auditorsReportForm.value;

    this.messageService.add({
      severity: 'success',
      summary: 'Form Submitted',
      detail: "Auditor's report has been saved successfully",
      life: TOAST_DURATION_DEFAULT_MS,
    });
  }

  /**
   * Get dropdown options for a field
   */
  getDropdownOptions(enumSource?: string): any[] {
    if (!enumSource) return [];

    const match = enumSource.match(/^type:(.+)$/);
    if (match && match[1]) {
      return this.dropdownOptions[match[1]] || [];
    }

    return [];
  }

  /**
   * Check if a field should be shown
   */
  shouldShowField(control: FormControlSchema): boolean {
    if (control.conditional) {
      return evaluateConditional(
        control.conditional,
        this.auditorsReportForm.getRawValue(),
        { filingInfo: this.stateService.getActiveFilingInfoData() }
      ).visible;
    }
    return true;
  }

  /**
   * Check if a field is required
   */
  isFieldRequired(control: FormControlSchema): boolean {
    return isControlMandatory(control as any, {
      selfData: this.auditorsReportForm?.getRawValue() ?? {},
      filingInfo: this.stateService.getActiveFilingInfoData() as any,
    });
  }

  /**
   * Check if a field has an error
   */
  hasError(fieldName: string): boolean {
    const control = this.auditorsReportForm.get(fieldName);
    return !!(control && control.invalid && (control.dirty || control.touched));
  }

  /**
   * Get error message for a field
   */
  getErrorMessage(fieldName: string): string {
    const control = this.auditorsReportForm.get(fieldName);
    if (control && control.errors) {
      if (control.errors['required']) {
        return 'This field is required';
      }
      if (control.errors['pattern']) {
        return 'Invalid format';
      }
    }
    return '';
  }

  /**
   * Get schema control by qname
   */
  getSchemaControl(qname: string): FormControlSchema | undefined {
    if (!this.schema || !this.schema.controls) return undefined;
    return this.schema.controls.find(control => control.qname === qname);
  }

  /**
   * Get custom error message from schema
   */
  getCustomErrorMessage(qname: string, errorType: string): string | null {
    const schemaControl = this.getSchemaControl(qname);
    if (schemaControl && (schemaControl as any).error && (schemaControl as any).error[errorType]) {
      return (schemaControl as any).error[errorType];
    }
    return null;
  }

  /**
   * Get form control by qname
   */
  getControl(qname: string): any {
    return this.auditorsReportForm.get(qname);
  }

  /**
   * Get all fields that belong to the auditor table
   */
  getAuditorTableFields(): FormControlSchema[] {
    if (!this.schema || !this.schema.controls) {
      return [];
    }
    return this.schema.controls.filter(
      (control) => control.ui.auditorTable === true
    );
  }

  /**
   * Get unique row keys from auditor table fields
   */
  getAuditorTableRows(): string[] {
    const auditorFields = this.getAuditorTableFields();
    const rowKeys = new Set<string>();

    auditorFields.forEach((field) => {
      if (field.ui.auditorRow) {
        rowKeys.add(field.ui.auditorRow);
      }
    });

    // Return rows in the order they appear in the schema
    return Array.from(rowKeys);
  }

  /**
   * Get fields for a specific row in the auditor table
   */
  /**
   * The qname identifying an auditor-table row, for the field-link `data-qname`
   * anchor — or undefined when the row has no field.
   *
   * Done here rather than as `getAuditorTableFieldsByRow(rowKey)[0]?.qname` in
   * the template because this project does not enable `noUncheckedIndexedAccess`,
   * so the compiler types `[0]` as always-present and reports the `?.` as a
   * redundant optional chain (NG8107) — while the array genuinely can be empty.
   * Mirrors the existing `getAuditorRowLabel` guard.
   */
  getAuditorRowQname(rowKey: string): string | undefined {
    const field = this.getAuditorTableFieldsByRow(rowKey)[0];
    return field ? field.qname : undefined;
  }

  getAuditorTableFieldsByRow(rowKey: string): FormControlSchema[] {
    const auditorFields = this.getAuditorTableFields();
    return auditorFields.filter((field) => field.ui.auditorRow === rowKey);
  }

  /**
   * Get the label for a specific row
   */
  getAuditorRowLabel(rowKey: string): string {
    const field = this.getAuditorTableFieldsByRow(rowKey)[0];
    return field ? field.label : rowKey;
  }

  /**
   * Does this Auditor Information ROW's label get a `*`?
   *
   * The table is the one place on this sheet where a label is derived from a
   * ROW KEY (`ui.auditorRow`) rather than rendered from a control — so the
   * schema-loop marker above never reached it, and its eight mandatory rows
   * (licence number, auditor name, firm registration number, firm name, address
   * line 1, postcode, town, state) showed no `*` while address lines 2 and 3,
   * which are genuinely optional, looked identical to them.
   *
   * A row is required when ANY of its controls is. Today every row maps to
   * exactly one control, so `.some()` is equivalent to checking the first — it
   * is written this way because `getAuditorRowLabel` already takes the FIRST
   * control's label, and a future multi-control row must not inherit a marker
   * decided by whichever control happens to sort first.
   */
  isAuditorRowRequired(rowKey: string): boolean {
    return this.getAuditorTableFieldsByRow(rowKey).some((control) =>
      this.isFieldRequired(control)
    );
  }

  /**
   * Setup warning evaluation system
   * Watches form changes and evaluates warnings for each field
   */
  private setupWarningEvaluation(): void {
    this.auditorsReportForm.valueChanges
      .pipe(
        debounceTime(FORM_AUTOSAVE_DEBOUNCE_MS),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.evaluateFieldWarnings();
      });
  }

  /**
   * Setup listener for cross-sheet validation
   * Listens to directorsReport and filingPrerequisites state changes to trigger warning re-evaluation
   */
  private setupCrossSheetWarningListener(): void {
    // Listen to directorsReport changes (for director's date comparison)
    this.stateService.state$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.auditorsReport)),
        map((state) => state.directorsReport),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((directorsReportData) => {
        if (directorsReportData && !this.isLoadingState) {
          this.evaluateFieldWarnings();
        }
      });

    // Listen to filingPrerequisites changes (for audit status condition)
    this.stateService.state$
      .pipe(
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.auditorsReport)),
        // BOTH filing-info slots. Role 0 (`filingPrerequisites`) is the editable
        // slot in Phase 1 ONLY; Phase 2 writes to role 1 (`filingInfo`) and
        // leaves the role-0 slot an empty stub, so watching role 0 alone never
        // fired here. Guarded by `npm run check:filing-info-watch`.
        map((state) => ({
          filingPrerequisites: state.filingPrerequisites,
          filingInfo: state.filingInfo,
        })),
        distinctUntilChanged(
          (prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((filingInfoSlices) => {
        if (
          (filingInfoSlices.filingPrerequisites || filingInfoSlices.filingInfo) &&
          !this.isLoadingState
        ) {
          this.evaluateFieldWarnings();
        }
      });

    // ----------------------------------
    // Cross-tab safety net — when directorsReport or filingPrerequisites is
    // mutated remotely (sibling tab edits), re-run evaluateFieldWarnings()
    // so the auditor's date / audit-status warnings stay in sync.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.auditorsReport)),
        filter(() => !!this.schema && !!this.auditorsReportForm),
        takeUntil(this.destroy$)
      )
      .subscribe(({ sheetKey }) => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role7-auditorsReport',
          //   sourceSheetKey: sheetKey,
          // });
        }
        this.evaluateFieldWarnings();
      });
    // ----------------------------------

    // ----------------------------------
    // Cross-tab live adoption — when a sibling tab edits THIS sheet's own
    // auditorsReport slice, `applyRemoteChange` rehydrates the shared state
    // and publishes `changedSheets` on `remoteUpdateApplied$`.
    // `adoptRemoteState()` below repaints this sheet's form from that state
    // (silently, no emitted valueChanges).
    //
    // Deliberately a SEPARATE subscription from the cross-tab safety net
    // above, but that safety net does NOT filter on `changedSheets` — it has
    // no slice filter at all — so it fires on EVERY remote `modifiedState`
    // update, including one that changed THIS sheet's own slice. It is
    // registered first, so on an own-slice remote update it runs
    // `evaluateFieldWarnings()` once against the still-STALE, pre-adoption
    // form, then this subscription runs `adoptRemoteState()`. The end state
    // is correct; the intermediate evaluation between the two is
    // wrong-valued for one microtask. Deliberately NOT deduped — deduping
    // risks dropping the upstream-driven refresh the safety net exists for
    // (directorsReport/filingPrerequisites slice changes that never touch
    // `changedSheets` for this sheet at all); see fix-wave I4.
    this.stateService.remoteUpdateApplied$
      .pipe(
        filter(({ streamKey }) => streamKey === 'modifiedState'),
        filter(({ changedSheets }) => changedSheets.includes(SHEET_KEYS.auditorsReport)),
        filter(() => !this.stateService.isSheetInitializing(SHEET_KEYS.auditorsReport)),
        filter(() => !!this.schema && !!this.auditorsReportForm),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        if (this.envService.isCrossTabSyncDebugEnabled()) {
          // console.log('[CrossTabSync][remote-validation-triggered]', {
          //   receiver: 'role7-auditorsReport',
          // });
        }
        this.adoptRemoteState();
      });
    // ----------------------------------
  }

  /**
   * Cross-tab adoption: a sibling tab edited this sheet, `applyRemoteChange` has
   * already rehydrated the shared state, and this repaints the form from it.
   *
   * MUST NOT write back to state — see the invariant on `adoptIntoAutoSaveSheet`.
   * The validation tail below runs for DISPLAY only; the sibling already
   * persisted its own results.
   *
   * Developer Note (TODO): silent adoption is the P.O. decision of 2026-08-17 and
   * is revisitable. If feedback is later wanted, the two options considered were a
   * brief per-row highlight (preferred — it answers "why did that number change?"
   * at the spot it changed) and a per-adoption toast (cheaper, but says nothing
   * about WHAT changed and stacks on repeated sibling edits).
   */
  private adoptRemoteState(): void {
    adoptIntoAutoSaveSheet(this.auditorsReportForm, this.stateService.getSheetData(SHEET_KEYS.auditorsReport));
    this.evaluateFieldWarnings();
  }

  /**
   * Evaluate field warnings for all fields with warning rules
   * Includes cross-sheet data for validation comparisons
   */
  private evaluateFieldWarnings(): void {
    // Only evaluate warnings in edited mode
    if (!this.isEditedMode()) {
      this.fieldWarnings = {};
      return;
    }

    // Get all form values for cross-field validation
    const allFormValues = this.auditorsReportForm.getRawValue();

    // Get cross-sheet data for validation
    const directorsReportData = this.stateService.getSheetData(SHEET_KEYS.directorsReport);

    // Structure cross-sheet data for FieldWarningService.
    // buildFilingInfoBagEntry() supplies the active filing-info slot under
    // both alias keys (filingPrerequisites + filingInfo) so phase-aware
    // resolution stays lossless across Phase 1 and Phase 2.
    const allValuesForWarnings = {
      ...allFormValues,
      ...this.stateService.buildFilingInfoBagEntry(),
      directorsReport: directorsReportData || {}
    };

    // Evaluate warnings for each field that has warning rules
    this.schema.controls.forEach((control) => {
      if (control.warnings && control.warnings.length > 0) {
        const formControl = this.auditorsReportForm.get(control.qname) as FormControl;
        if (formControl) {
          const warnings = this.warningService.evaluateWarnings(
            formControl,
            control.warnings,
            allValuesForWarnings,
            this.isEditedMode()
          );

          if (warnings.length > 0) {
            this.fieldWarnings[control.qname] = warnings;
          } else {
            // Clear warnings if none triggered
            delete this.fieldWarnings[control.qname];
          }
        }
      }
    });

  }

  /**
   * Get validation warnings for a specific field
   * Returns array of warning results (BLACK text with asterisk)
   */
  getFieldWarnings(qname: string): WarningResult[] {
    return this.fieldWarnings[qname] || [];
  }

  /**
   * Check if field has any validation warnings
   * Used to show asterisk (*) next to field label in edited mode
   */
  hasWarnings(qname: string): boolean {
    return this.fieldWarnings[qname]?.length > 0;
  }

  /**
   * Get all warnings for a specific field (for app-warning-message component)
   * Returns array of warning objects with message and severity properties for display
   * Sorted: errors first, then warnings
   */
  getAllWarnings(qname: string): WarningMessage[] {
    const warnings: WarningMessage[] = [];
    const fieldWarnings = this.fieldWarnings[qname] || [];
    fieldWarnings.forEach(warning => {
      // Map severity to 'warning' | 'error' (treat 'info' as 'warning')
      const severity: 'warning' | 'error' = warning.severity === 'error' ? 'error' : 'warning';
      warnings.push({ message: warning.message, severity });
    });

    // Sort: errors first, then warnings
    return warnings.sort((firstWarning, secondWarning) => {
      if (firstWarning.severity === 'error' && secondWarning.severity !== 'error') return -1;
      if (firstWarning.severity !== 'error' && secondWarning.severity === 'error') return 1;
      return 0;
    });
  }

  /**
   * Split warning message into parts (only asterisk * is red, parentheses stay black)
   * Returns array of objects with text and isAsterisk flag for rendering
   * Example: "(*) Some text" → ["(", "*", ")", " Some text"]
   */
  splitWarningMessage(message: string): { text: string; isAsterisk: boolean }[] {
    const parts: { text: string; isAsterisk: boolean }[] = [];
    const regex = /\(\*\)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(message)) !== null) {
      // Add text before (*)
      if (match.index > lastIndex) {
        parts.push({
          text: message.substring(lastIndex, match.index),
          isAsterisk: false
        });
      }

      // Add ( - black
      parts.push({
        text: '(',
        isAsterisk: false
      });

      // Add * - red
      parts.push({
        text: '*',
        isAsterisk: true
      });

      // Add ) - black
      parts.push({
        text: ')',
        isAsterisk: false
      });

      lastIndex = regex.lastIndex;
    }

    // Add remaining text after last (*)
    if (lastIndex < message.length) {
      parts.push({
        text: message.substring(lastIndex),
        isAsterisk: false
      });
    }

    return parts;
  }

  onInputChange(event: InputEvent) {
    handleDateInputChange(event, this.auditorsReportForm);
  }
  
  /**
   * `(input)` / `(paste)` handlers for controls that declare `ui.inputTransform`
   * (strip whitespace, uppercase). Bound on the auditor-table text input; both are
   * no-ops for a control without the flag. See utils/input-transform.util.ts.
   */
  applyInputTransform(event: Event, control: FormControlSchema): void {
    applyInputTransformToField(
      event,
      control.ui.inputTransform,
      control.ui.maxLength,
      control.qname,
      this.auditorsReportForm
    );
  }

  onPasteInputTransform(event: ClipboardEvent, control: FormControlSchema): void {
    pasteInputTransformToField(
      event,
      control.ui.inputTransform,
      control.ui.maxLength,
      control.qname,
      this.auditorsReportForm
    );
  }

  /**
   * Get max length for a field
   * Uses shared helper function from utils/helpers.ts
   */
  getMaxLength(fieldName: string): number | null {
    return getFieldMaxLength(fieldName, this.schema);
  }

  /**
   * Determine if an identification number field should be numeric-only
   * Returns true if the corresponding type field is MyKad/MyPR/MyKAS
   */
  shouldBeNumericOnly(fieldName: string): boolean {
    const typeValue = this.auditorsReportForm.get(identificationTypeFieldFor(fieldName))?.value;
    return isNumericOnlyIdentificationField(fieldName, typeValue);
  }

  /**
   * Get the appropriate KeyFilter pattern for a field
   * Returns regex pattern that only allows digits (no negative, no decimal)
   */
  getKeyFilter(control: FormControlSchema): RegExp | null {
    // For numeric inputFilter, use regex that only allows digits (no negative, no decimal)
    if (control.ui.inputFilter === 'numeric') {
      return /^[0-9]*$/;
    }

    // For identification number fields
    if (this.shouldBeNumericOnly(control.qname)) {
      return /^[0-9]*$/;
    }

    return null;
  }

  onCancelConfirmed(): void {
    this.loadSavedState();
    this.loadTraceHandle?.mark(SHEET_LOAD_STEPS.stateRestored);
    this.auditorsReportForm.markAsPristine();

    // Re-finalize after the revert: the legacy setTimeout inside loadSavedState
    // used to do this on every call; now finalizeInit() owns it.
    void this.finalizeInit();
  }
}
